import ImageModal from "components/ImageModal";
import SuspectDetailInfo from "pages/Incident/IncidentDetail/modals/SuspectDetailInfo";
import AppealRequestFromDefendantForm from "pages/InstituteOfCharge/modal/AppealRequestFromDefendantForm";
import DefendantAppealRequestForm from "pages/InstituteOfCharge/modal/DefendantAppealRequestForm";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  AmmendedCourtDecisionType,
  AppealDailyReport,
  AppealDailyReportStatus,
  AppealFinalVerdict,
  DefendantAppeal,
} from "types";
import AppealAddDailyReport from "./fragment/AppealAddDailyReport";
import AppealAddFinalVerdict from "./fragment/AppealAddFinalVerdict";
import AppealApplicationTab from "./fragment/AppealApplicationTab";
import FinalVerdictDecisionUpload from "./fragment/FinalVerdictDecisionUpload";

const verdictDecisionColor = (type?: string) => {
  switch (type) {
    case "UPHELD":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "OVERTURNED":
      return "bg-red-50 text-red-700 border-red-200";
    case "MODIFIED":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "REMANDED":
      return "bg-purple-50 text-purple-700 border-purple-200";
    default:
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
};

type Props = {
  daily_report_defendant?: AppealDailyReport[];
  final_verdict_defendant?: AppealFinalVerdict[];
  setSelectedAppealDailyReport: React.Dispatch<
    React.SetStateAction<AppealDailyReport | undefined>
  >;
  selectedAppealDailyReport?: AppealDailyReport;
  isProsecutor?: boolean;
  isRegistrar?: boolean;
  isCoordinator?: boolean;
  appealData: DefendantAppeal;
  fetchData: () => void;
};

// Reusable Components
const EmptyState: React.FC<{
  title: string;
  description: string;
  icon?: React.ReactNode;
}> = ({ title, description, icon }) => (
  <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl border border-gray-200">
    {icon || (
      <svg
        className="w-16 h-16 text-gray-300 mb-4"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 17v-2a4 4 0 018 0v2M12 7a4 4 0 110 8 4 4 0 010-8zm0 0V3m0 4a4 4 0 100 8 4 4 0 000-8z"
        />
      </svg>
    )}
    <p className="text-gray-500 text-lg font-medium mb-2">{title}</p>
    <p className="text-gray-400 text-sm text-center max-w-md">{description}</p>
  </div>
);

const StatusBadge: React.FC<{
  status: keyof typeof AppealDailyReportStatus;
  t: (key: string) => string;
}> = ({ status, t }) => (
  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
    {t(`status_${status.toLowerCase()}`) || AppealDailyReportStatus[status]}
  </span>
);

const DefendantAppealReport: React.FC<Props> = ({
  setSelectedAppealDailyReport,
  selectedAppealDailyReport,
  isProsecutor = false,
  isRegistrar = false,
  isCoordinator = false,
  appealData,
  fetchData,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Cassation records read "Cassation" instead of "Appeal".
  const isCass = !!(appealData as any)?.is_casations;
  const [selectedEntity, setSelectedEntity] = useState<any>();
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState({
    suspect: false,
    witness: false,
  });
  // Bumped after submit/cancel to remount (reset) the right-column form.
  const [formKey, setFormKey] = useState(0);
  const [activeTab, setActiveTab] = useState<
    "dailyReport" | "finalVerdict" | "courtApplication"
  >("dailyReport");
  const [openVerdictIndex, setOpenVerdictIndex] = useState<number | null>(null);
  const [openDailyIndexes, setOpenDailyIndexes] = useState<number[]>([]);
  const [openAppealForm, setOpenAppealForm] = useState(false);
  const [openCassationForm, setOpenCassationForm] = useState(false);

  // Daily Report Item Component
  const DailyReportItem: React.FC<{
    report: AppealDailyReport;
    index: number;
  }> = ({ report, index }) => {
    const open = openDailyIndexes.includes(index);
    const selected = selectedAppealDailyReport?.id === report.id;
    return (
      <div
        className={`mb-3 bg-white rounded-xl border overflow-hidden transition-colors ${
          selected ? "border-red-300 ring-1 ring-red-200" : "border-gray-200"
        }`}
      >
        {/* Accordion header */}
        <button
          type="button"
          onClick={() => {
            setOpenDailyIndexes((prev) =>
              prev.includes(index)
                ? prev.filter((i) => i !== index)
                : [...prev, index],
            );
            setSelectedAppealDailyReport(report);
          }}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 text-left"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 shrink-0 bg-red-50 rounded-lg flex items-center justify-center">
              <span className="text-red-600">📅</span>
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 text-sm">
                {t("daily_report")} #{index + 1}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {report?.report_date
                  ? new Date(report.report_date).toLocaleDateString()
                  : t("n_a")}
                {report?.next_trial_appointment && (
                  <>
                    {" · "}
                    {t("next_trial_appointment")}:{" "}
                    {new Date(
                      report.next_trial_appointment,
                    ).toLocaleDateString()}
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {report?.is_final_verdict_given && (
              <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                ✓ {t("final_verdict")}
              </span>
            )}
            {report?.status && <StatusBadge status={report.status} t={t} />}
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${
                open ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>

        {/* Accordion body */}
        {open && (
          <div className="px-4 pb-4 pt-3 border-t border-gray-100 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {[
                {
                  label: t("report_date"),
                  value: report?.report_date
                    ? new Date(report.report_date).toLocaleDateString()
                    : "",
                },
                {
                  label: t("next_trial_appointment"),
                  value: report?.next_trial_appointment
                    ? new Date(
                        report.next_trial_appointment,
                      ).toLocaleDateString()
                    : "",
                },
                {
                  label: t("appointment_type"),
                  value: report?.appointment_type,
                },
                {
                  label: t("appointment_reason"),
                  value: report?.appointment_reason,
                },
                {
                  label: t("status"),
                  value: report?.status
                    ? AppealDailyReportStatus[report.status]
                    : "",
                },
                {
                  label: t("is_final_verdict_given"),
                  value: report?.is_final_verdict_given
                    ? t("yes", { defaultValue: "Yes" })
                    : t("no", { defaultValue: "No" }),
                },
              ].map((f, i) => (
                <div key={i} className="flex flex-col">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    {f.label}
                  </span>
                  <span className="text-sm font-medium text-gray-800">
                    {f.value || t("n_a")}
                  </span>
                </div>
              ))}
            </div>

            <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                {t("daily_report_summary")}
              </p>
              <p className="text-sm text-gray-800 whitespace-pre-line">
                {report?.dailyreport_summary || t("no_summary_available")}
              </p>
            </div>

            <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                {t("court_order_for_next_appointment")}
              </p>
              <p className="text-sm text-gray-800 whitespace-pre-line">
                {report?.court_order_for_next_appointment ||
                  t("no_court_order_available")}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Final Verdict Item Component
  const FinalVerdictItem: React.FC<{
    report: AppealFinalVerdict;
    index: number;
  }> = ({ report, index }) => {
    const isOpen = openVerdictIndex === index;
    const decisionType = report?.amended_court_decision_type;
    const showAmended =
      !!decisionType && !["UPHELD", "REMANDED"].includes(decisionType);
    const defendantNames = (report?.defendants || [])
      .map((d: any) =>
        [
          d?.defendant_info?.first_name,
          d?.defendant_info?.middle_name,
          d?.defendant_info?.last_name,
        ]
          .filter(Boolean)
          .join(" "),
      )
      .filter(Boolean)
      .join(", ");
    const defendantLabel = report?.defendant_name || defendantNames;

    return (
      <div className="mb-3 bg-white rounded-xl border-2 border-emerald-200 overflow-hidden">
        {/* Accordion header */}
        <button
          type="button"
          onClick={() => setOpenVerdictIndex(isOpen ? null : index)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-emerald-50/40 text-left"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 shrink-0 bg-emerald-100 rounded-lg flex items-center justify-center">
              <span className="text-emerald-600">🎯</span>
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 text-sm">
                {isCass
                  ? t("cassation_final_verdict", {
                      defaultValue: "Cassation Final Verdict",
                    })
                  : t("appeal_final_verdict")}{" "}
                #{index + 1}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {defendantLabel || t("n_a")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {decisionType && (
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${verdictDecisionColor(
                  decisionType,
                )}`}
              >
                {(AmmendedCourtDecisionType as any)[decisionType] ||
                  decisionType}
              </span>
            )}
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>

        {/* Accordion body */}
        {isOpen && (
          <div className="px-4 pb-4 pt-3 border-t border-gray-100 space-y-3">
            {/* Defendants */}
            {report?.defendants && report.defendants.length > 0 && (
              <div className="space-y-2">
                {report.defendants.map((def, defIndex) => (
                  <div
                    key={defIndex}
                    className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg border border-gray-100"
                  >
                    <img
                      src={
                        def?.defendant_info?.picture_file ||
                        require("../../images/imageVector.png")
                      }
                      alt="Defendant"
                      className="w-10 h-10 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => {
                        setIsImageModalOpen(true);
                        setSelectedEntity(def?.defendant_info);
                      }}
                    />
                    <p className="flex-1 min-w-0 text-gray-900 font-medium text-sm truncate">
                      {`${def?.defendant_info?.first_name} ${def?.defendant_info?.middle_name} ${def?.defendant_info?.last_name}`}
                    </p>
                    <button
                      onClick={() => {
                        setSelectedEntity(def?.defendant_info);
                        setIsDetailModalOpen((prev) => ({
                          ...prev,
                          suspect: true,
                        }));
                      }}
                      className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-white rounded-lg transition-colors"
                      title={t("view_defendant_details")}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {[
                { label: t("defendant"), value: defendantLabel },
                {
                  label: t("amended_decision_type"),
                  value: decisionType
                    ? (AmmendedCourtDecisionType as any)[decisionType] ||
                      decisionType
                    : "",
                },
                ...(showAmended
                  ? [
                      {
                        label: t("amended_decision"),
                        value: report?.amended_court_decision,
                      },
                      {
                        label: t("amended_decision_amount"),
                        value: report?.amended_court_decision_amount,
                      },
                    ]
                  : []),
              ].map((f, i) => (
                <div key={i} className="flex flex-col">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    {f.label}
                  </span>
                  <span className="text-sm font-medium text-gray-800">
                    {f.value || t("n_a")}
                  </span>
                </div>
              ))}
            </div>

            <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                {t("court_decision_summary")}
              </p>
              <p className="text-sm text-gray-800 whitespace-pre-line">
                {report?.court_decision_summary || t("no_summary_available")}
              </p>
            </div>

            <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                {t("prosecutor_feedback")}
              </p>
              <p className="text-sm text-gray-800 whitespace-pre-line">
                {report?.prosecutor_feedback_on_decision ||
                  t("no_feedback_available")}
              </p>
            </div>

            {/* Final court-decision file — uploaded after creation */}
            <FinalVerdictDecisionUpload
              verdictId={report?.id}
              documents={(report as any)?.documents}
              canUpload={!!isProsecutor}
              onUploaded={fetchData}
            />

            {(isProsecutor || isRegistrar) && (
              <div className="flex flex-wrap justify-end gap-3 pt-1">
                {isProsecutor && (
                  <button
                    onClick={() => setOpenAppealForm(true)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
                  >
                    {t("request_appeal")}
                  </button>
                )}
                {isRegistrar && (
                  <button
                    onClick={() => setOpenCassationForm(true)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700"
                  >
                    {t("request_defendant_cassation", {
                      defaultValue: "Request Defendant Cassation",
                    })}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Litigation is finished once every defendant has a final verdict — then we
  // stop allowing new daily reports / final verdicts.
  const verdictDefendantIds = new Set<string>();
  (appealData?.final_verdict_defendant || []).forEach((v: any) => {
    const ids = Array.isArray(v?.defendant_ids)
      ? v.defendant_ids
      : (v?.defendants || []).map((d: any) => d?.id);
    (ids || []).forEach((id: any) =>
      verdictDefendantIds.add(String(typeof id === "object" ? id?.id : id)),
    );
  });
  const appealDefendantIds = ((appealData as any)?.defendants || []).map(
    (d: any) => String(d?.id),
  );
  const allFinalVerdictsSubmitted =
    appealDefendantIds.length > 0 &&
    appealDefendantIds.every((id: string) => verdictDefendantIds.has(id));

  const finishedNotice = (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center">
      <div className="text-2xl mb-1">✅</div>
      <p className="text-sm font-semibold text-emerald-800">
        {t("all_final_verdicts_submitted", {
          defaultValue: "All final verdicts have been submitted.",
        })}
      </p>
      <p className="text-xs text-emerald-700/80 mt-1">
        {t("litigation_finished_note", {
          defaultValue:
            "Litigation is finished — no further daily reports or verdicts can be added.",
        })}
      </p>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              {t("report_type")}
            </label>
            <div className="flex space-x-1 p-1 bg-gray-100 rounded-lg w-fit">
              <button
                onClick={() => setActiveTab("dailyReport")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === "dailyReport"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                📅 {t("daily_report")}
              </button>
              <button
                onClick={() => setActiveTab("finalVerdict")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === "finalVerdict"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                ⚖️ {t("final_verdict")}
              </button>
              <button
                onClick={() => setActiveTab("courtApplication")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === "courtApplication"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                🏛️ {t("send_to_court", { defaultValue: "Send to Court" })}
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Content Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="max-h-[65vh] overflow-y-auto pr-4">
          {activeTab === "dailyReport" && (
            <>
              {appealData?.daily_report_defendant &&
              appealData.daily_report_defendant.length > 0 ? (
                appealData.daily_report_defendant
                  .slice()
                  .sort((a, b) => {
                    // Chronological — oldest first (by report date).
                    const dateA = a.report_date
                      ? new Date(a.report_date).getTime()
                      : a.next_trial_appointment
                        ? new Date(a.next_trial_appointment).getTime()
                        : 0;
                    const dateB = b.report_date
                      ? new Date(b.report_date).getTime()
                      : b.next_trial_appointment
                        ? new Date(b.next_trial_appointment).getTime()
                        : 0;
                    return dateA - dateB;
                  })
                  .map((report, index) => (
                    <DailyReportItem
                      key={index}
                      report={report}
                      index={index}
                    />
                  ))
              ) : (
                <EmptyState
                  title={t("no_daily_report_data")}
                  description={t("no_daily_report_description")}
                />
              )}
            </>
          )}

          {activeTab === "finalVerdict" && (
            <>
              {appealData?.final_verdict_defendant &&
              appealData.final_verdict_defendant.length > 0 ? (
                appealData.final_verdict_defendant.map((report, index) => (
                  <FinalVerdictItem key={index} report={report} index={index} />
                ))
              ) : (
                <EmptyState
                  title={t("no_final_verdict_data")}
                  description={t("no_final_verdict_description")}
                  icon={
                    <svg
                      className="w-16 h-16 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  }
                />
              )}
            </>
          )}

          {activeTab === "courtApplication" && appealData?.id && (
            <AppealApplicationTab
              mode="list"
              defendantAppealRequestId={String(appealData.id)}
              isProsecutor={isProsecutor}
              isCoordinator={isCoordinator}
              isRegistrar={isRegistrar}
              refreshSignal={formKey}
              onChanged={() => setFormKey((k) => k + 1)}
            />
          )}
          </div>
        </div>

        {/* Right — details panel for non-prosecutor roles. */}
        {!isProsecutor && appealData && (
          <div className="lg:sticky lg:top-0">
            <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-base">ℹ️</span>
                <h4 className="text-sm font-semibold text-gray-700">
                  {t("details", { defaultValue: "Details" })}
                </h4>
              </div>
              <p className="text-sm text-gray-500">
                {activeTab === "courtApplication"
                  ? t("court_application_role_hint", {
                      defaultValue:
                        "Open an application on the left to review it and take action — forward, approve, send to court, or attach the court response.",
                    })
                  : t("report_view_hint", {
                      defaultValue:
                        "Open an item on the left to view its full details.",
                    })}
              </p>
            </div>
          </div>
        )}

        {/* Right — submit form (prosecutor only) */}
        {isProsecutor && appealData && (
          <div key={formKey} className="lg:sticky lg:top-0">
            {activeTab === "courtApplication" && appealData?.id && (
              <AppealApplicationTab
                mode="form"
                defendantAppealRequestId={String(appealData.id)}
                isProsecutor={isProsecutor}
                onChanged={() => setFormKey((k) => k + 1)}
              />
            )}
            {activeTab === "dailyReport" &&
              (allFinalVerdictsSubmitted ? (
                finishedNotice
              ) : (
                <AppealAddDailyReport
                  inline
                  defendantAppealData={appealData}
                  onClose={(reload) => {
                    if (reload) fetchData();
                    setFormKey((k) => k + 1);
                  }}
                />
              ))}
            {activeTab === "finalVerdict" &&
              (allFinalVerdictsSubmitted ? (
                finishedNotice
              ) : (
                <AppealAddFinalVerdict
                  inline
                  defendantAppealData={appealData}
                  onClose={(reload) => {
                    if (reload) fetchData();
                    setFormKey((k) => k + 1);
                  }}
                />
              ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {isImageModalOpen && (
        <ImageModal
          isOpen={isImageModalOpen}
          imageSrc={
            selectedEntity?.picture || require("../../images/imageVector.png")
          }
          label={`${selectedEntity?.first_name} ${selectedEntity?.middle_name} ${selectedEntity?.last_name}`}
          onClose={() => setIsImageModalOpen(false)}
        />
      )}

      {isDetailModalOpen?.suspect && (
        <SuspectDetailInfo
          suspect={selectedEntity}
          isOpen={isDetailModalOpen?.suspect}
          isDefendant={true}
          onClose={() =>
            setIsDetailModalOpen({ suspect: false, witness: false })
          }
        />
      )}

      {openAppealForm && appealData && (
        <AppealRequestFromDefendantForm
          appeal={appealData}
          appealType={"ReAppealCase"}
          onClose={(reload) => {
            setOpenAppealForm(false);
            if (reload) navigate("/appeal");
          }}
        />
      )}
      {openCassationForm && appealData && (
        <DefendantAppealRequestForm
          isCassation
          prefillAppeal={appealData as any}
          parentDefendantAppealId={appealData?.id?.toString()}
          onClose={(reload) => {
            setOpenCassationForm(false);
            if (reload) navigate("/defendant-cassation");
          }}
        />
      )}
    </div>
  );
};

export default DefendantAppealReport;
